import type { ThemeOptions } from '@mui/material/styles';
import { APP_FONT_FAMILY_VAR } from './fonts';

export { APP_TEXT_COLOR } from './colors';

/**
 * Typography matches the Zero platform: Inter + Noto Sans multi-script stack
 * with MUI's default type scale (14px theme base).
 *
 * Local style modules may still use these tokens for tables / compact UI.
 * Values follow MUI defaults so explicit sizes stay aligned with the theme.
 */
export const APP_FONT_SIZE = {
  xs: '0.75rem', // 12px — caption
  sm: '0.875rem', // 14px — body2 / tables / compact UI
  md: '1rem', // 16px — body1
  lg: '1.25rem',
  xl: '2.125rem', // h4
} as const;

export const APP_LINE_HEIGHT = {
  tight: 1.235,
  normal: 1.5,
  relaxed: 1.75,
} as const;

/** Weight + Zero/Ops font stack; heading sizes stay MUI defaults. */
export const appTypography: ThemeOptions['typography'] = {
  fontFamily: APP_FONT_FAMILY_VAR,
  h1: { fontWeight: 700 },
  h2: { fontWeight: 700 },
  h3: { fontWeight: 600 },
  h4: { fontWeight: 600 },
  h5: { fontWeight: 600 },
  h6: { fontWeight: 600 },
};

/**
 * Content-area type scale (Readex Pro). Hierarchy:
 * page title (h4) → section title (h6, title case in English) → description
 * (body2) → KPI figure (`ESG_FIGURE`) → label (overline) → metadata (caption).
 *
 * Labels are normal case with no tracking in every language (no all-caps);
 * `latin` only gates the negative tracking on large headings.
 */
export function createContentTypography(latin: boolean): ThemeOptions['typography'] {
  return {
    fontFamily: APP_FONT_FAMILY_VAR,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    h1: { fontSize: '2rem', fontWeight: 600, lineHeight: 1.2, letterSpacing: latin ? '-0.015em' : 0 },
    h2: { fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.2, letterSpacing: latin ? '-0.01em' : 0 },
    h3: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.25, letterSpacing: latin ? '-0.01em' : 0 },
    h4: { fontSize: '1.625rem', fontWeight: 600, lineHeight: 1.25, letterSpacing: latin ? '-0.01em' : 0 },
    h5: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.35 },
    h6: { fontSize: '1.0625rem', fontWeight: 600, lineHeight: 1.4 },
    subtitle1: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.5 },
    subtitle2: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.45 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.55 },
    caption: { fontSize: '0.75rem', lineHeight: 1.45 },
    overline: {
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: 1.45,
      letterSpacing: 0,
      textTransform: 'none',
    },
    button: { fontSize: '0.875rem', fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
  };
}

/**
 * Editorial figure sizes for KPI numbers. Combine with `ESG_FIGURE_BASE`; the
 * number itself carries the visual weight, not an icon or a coloured card.
 */
export const ESG_FIGURE = {
  hero: { fontSize: { xs: '2.25rem', md: '3rem' }, lineHeight: 1.05 },
  lg: { fontSize: { xs: '1.75rem', md: '2rem' }, lineHeight: 1.1 },
  md: { fontSize: '1.5rem', lineHeight: 1.15 },
  sm: { fontSize: '1.25rem', lineHeight: 1.2 },
} as const;

export type EsgFigureSize = keyof typeof ESG_FIGURE;

export const ESG_FIGURE_BASE = {
  fontWeight: 600,
  fontVariantNumeric: 'tabular-nums lining-nums',
  fontFeatureSettings: '"tnum" 1, "lnum" 1',
} as const;

export const appTypographyComponents: ThemeOptions['components'] = {
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        fontFamily: APP_FONT_FAMILY_VAR,
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 600,
      },
    },
  },
};
